import React from 'react'
import { AxiosError } from 'axios'
import { Link } from 'react-router-dom'
import { Tooltip } from 'antd'
import { Div, HorizontalLayout, PaperLayout } from '@nic/ui-comps'
import { useTranslation } from '@nic/commons'
import { Darwin, NicScope } from '@nic/nic-api/types'
import {
  DarwinHelpers,
  requestsCountInitialState,
  requestsExpiringCountReducer,
} from '@nic/nic-api'

import { useRequestCounter } from './useRequestCounter'
import { IAssignmentParam } from './types'
import { dashboardCounterDefaults } from './DashboardCounter.defaults'
import './Counter.css'

export type CounterValueKind = 'requests' | 'expiring' | 'static' | 'none'

export interface ICounterValueConfig {
  label: string
  kind: CounterValueKind
  status?: Darwin.RequestStatus
  assignment?: IAssignmentParam
  registrarId?: number
  days?: number
  value?: number
  redirect?: string
  highlight?: boolean
  enabled?: boolean
}

interface IDashboardCounter {
  title: React.ReactNode
  icon?: React.ReactElement<any>
  scope: NicScope
  backgroundColor?: string
  className?: string
  tip?: string
  titleSize?: string
  labelSize?: string
  valueSize?: string
  titleBarColor?: string
  titleBarIcon?: React.ReactNode
  titleBarTextColor?: string
  showTitleBar?: boolean
  showBodyIcon?: boolean
  primary: ICounterValueConfig
  secondary?: ICounterValueConfig
}

interface CounterState {
  data?: { count?: number }
  loading?: boolean
  error?: unknown
}

const useExpiringCounter = (
  scope: NicScope,
  days: number,
  assignment?: IAssignmentParam,
  enabled: boolean = true
) => {
  const [state, dispatch] = React.useReducer(
    requestsExpiringCountReducer,
    requestsCountInitialState
  )

  React.useEffect(() => {
    if (!enabled) return
    const params = assignment?.param
      ? ({ days, [assignment.param]: assignment.value } as any)
      : ({ days } as any)
    DarwinHelpers.getRequestsExpiringCount(dispatch, params, scope)
  }, [assignment?.param, assignment?.value, days, enabled, scope])

  return state as CounterState
}

const useCounterValueState = (config: ICounterValueConfig, scope: NicScope) => {
  const enabled = config.enabled !== false
  const isRequests = config.kind === 'requests'
  const isExpiring = config.kind === 'expiring'

  const requestState = useRequestCounter(
    scope,
    config.status,
    config.assignment,
    config.registrarId,
    enabled && isRequests
  ) as CounterState

  const expiringState = useExpiringCounter(
    scope,
    config.days ?? 7,
    config.assignment,
    enabled && isExpiring
  )

  if (config.kind === 'expiring') return expiringState
  return requestState
}

export const DashboardCounter: React.FC<IDashboardCounter> = ({
  title,
  icon,
  scope,
  backgroundColor,
  className,
  tip,
  titleSize = dashboardCounterDefaults.titleSize,
  labelSize = dashboardCounterDefaults.labelSize,
  valueSize = dashboardCounterDefaults.valueSize,
  titleBarColor = 'rgba(0, 0, 0, 0.2)',
  titleBarIcon,
  titleBarTextColor = '#ffffff',
  showTitleBar = true,
  showBodyIcon,
  primary,
  secondary,
}) => {
  const t = useTranslation()
  const primaryState = useCounterValueState(primary, scope)
  const secondaryConfig: ICounterValueConfig = secondary ?? {
    label: '',
    kind: 'none',
    enabled: false,
  }
  const secondaryState = useCounterValueState(secondaryConfig, scope)

  const formatValue = (config: ICounterValueConfig, state: CounterState) => {
    if (config.kind === 'static') return config.value ?? 0
    if (state?.error) {
      return t('someThingWrong') + (state.error as AxiosError)?.response?.status
    }
    if (state?.loading) return '...'
    return state?.data?.count ?? 0
  }

  const resolveStyle = (config: ICounterValueConfig, state: CounterState) => {
    if (config.kind !== 'static') {
      if (state?.error) return { color: 'red' }
      if (state?.loading) return { color: 'grey' }
    }
    return undefined
  }

  const ValueLink = ({
    to,
    disabled,
    style,
    className: valueClassName,
    children,
  }: {
    to?: string
    disabled: boolean
    style?: React.CSSProperties
    className?: string
    children: React.ReactNode
  }) => {
    const cls = `counter-value-link ${valueClassName ?? ''}`.trim()
    if (!to || disabled) {
      return (
        <span className={`counter-value-text ${valueClassName ?? ''}`.trim()} style={style}>
          {children}
        </span>
      )
    }
    return (
      <Link to={to} className={cls} style={style}>
        {children}
      </Link>
    )
  }

  const visibleValues = [primary, secondary].filter(
    (config) => config && config.enabled !== false && config.kind !== 'none'
  )
  const gridColumns = `repeat(${Math.max(1, visibleValues.length)}, minmax(0, 1fr))`

  const renderValue = (config: ICounterValueConfig, state: CounterState) => {
    if (!config || config.enabled === false || config.kind === 'none') return null
    const disabled = Boolean(state?.loading || state?.error)
    const valueStyle: React.CSSProperties = { fontSize: valueSize, ...resolveStyle(config, state) }
    const labelStyle: React.CSSProperties = { fontSize: labelSize }
    const highlightClass = config.highlight ? 'counter-value-link--assigned' : undefined

    return (
      <Div className="counter-split-column">
        <span className="counter-value-label" style={labelStyle}>
          {config.label}
        </span>
        <ValueLink
          to={config.redirect}
          disabled={disabled}
          style={valueStyle}
          className={highlightClass}
        >
          {formatValue(config, state)}
        </ValueLink>
      </Div>
    )
  }

  const resolvedIcon = icon ?? <span className="counter-default-icon" />
  const useTitleBar = showTitleBar
  const showIconInBody = showBodyIcon ?? !useTitleBar
  const renderTitle = (value: React.ReactNode) => {
    if (typeof value !== 'string') return value
    const match = value.match(/^(.+?)\s*(\(.+\))$/)
    if (!match) return value
    return (
      <span>
        {match[1]}
        <span className="counter-title-sub">{match[2]}</span>
      </span>
    )
  }

  const titleContent = (
    <span className="counter-title-bar-content">
      <span className="counter-title-bar-icon">{titleBarIcon ?? resolvedIcon}</span>
      <span className="counter-title-bar-text" style={{ fontSize: titleSize }}>
        {renderTitle(title)}
      </span>
    </span>
  )

  return (
    <PaperLayout
      asCard={true}
      data-cy="counter"
      backgroundColor={backgroundColor}
      className={className ? `counter-box ${className}` : 'counter-box'}
    >
      {useTitleBar && (
        <Div
          className="counter-title-bar"
          style={{ backgroundColor: titleBarColor, color: titleBarTextColor }}
        >
          {tip ? <Tooltip title={tip}>{titleContent}</Tooltip> : titleContent}
        </Div>
      )}
      <HorizontalLayout style={{ gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
        {showIconInBody && <Div className="counter-icon">{resolvedIcon}</Div>}
        <Div className="counter-body">
          {!useTitleBar && (
            <Div className="counter-title" style={{ fontSize: titleSize }}>
              {tip ? <Tooltip title={tip}>{renderTitle(title)}</Tooltip> : renderTitle(title)}
            </Div>
          )}
          <Div className="counter-values">
            <Div className="counter-split-grid" style={{ gridTemplateColumns: gridColumns }}>
              {renderValue(primary, primaryState)}
              {renderValue(secondaryConfig, secondaryState)}
            </Div>
          </Div>
        </Div>
      </HorizontalLayout>
    </PaperLayout>
  )
}
